/* eslint-disable @typescript-eslint/camelcase */
import { handleActions } from 'redux-actions';
import { ListAdvisorsDataProps } from 'modules/advisors/interfaces';
import {
  AvgMaxCountAccess,
  BehaviorReportProps,
  DNSReportProps,
  EnvironmentsState,
  InstanceStateEnum,
  MaintenanceReportProps,
  PeriodCountAccess,
  ProductReportProps,
  ProviderReportProps,
  PublicationResponse,
  PublishImageEnum,
  RegionReportProps,
  StatusReportProps,
  ValidateZeroTrustFields,
  VirtualizerReportProps,
  ZeroTrustReportProps,
  ListEnvironmentSessionsProps,
  ProductTypeReportProps,
  ListLicenseGroupsProps,
  LicenseGroupsFields,
  ConnectionTypeEnvironmentReportProps,
  AdminPinProps,
  InstanceObservabilityState,
} from './interfaces';
import Types from './types';

const initialState: EnvironmentsState = {
  isLoading: false,
  environmentSelected: null,
  isLoadingMaintenance: false,
  isLoadingLogs: true,
  modelSelected: null,
  environmentList: {
    count: 0,
    next: '',
    previous: '',
    results: [],
  },
  environmentsList: {
    count: 0,
    next: '',
    previous: '',
    results: [],
  },
  alarmsInstanceList: {
    count: 0,
    next: '',
    previous: '',
    results: [],
  },
  instancesList: {
    count: 0,
    next: '',
    previous: '',
    loading: false,
    results: [],
  },
  preparationServer: {
    instance_id: '',
    name: '',
    public_ip: '',
    private_ip: '',
    instance_state: InstanceStateEnum.PENDING,
    image_publication_status: PublishImageEnum.PENDING,
    flavor: '',
    server_type: 'preparation',
    stop_time: '',
    connection_usernames: [],
    is_application_server: false,
    has_image_publication_schedule: false,
    block_actions_enabled: false,
    unblock_time: null,
    block_reason: '',
    blocked_by: { first_name: '', last_name: '', email: '' },
    blocked_at: null,
    can_execute_actions_on_blocked_server: false,
    platform_version: null,
    is_stopping_monitoring: false,
    monitoring_management_enabled: false,
    isLoadingMonitoringStatus: false,
    monitoringStatus: '',
  },
  isLoadingPreparationServer: false,
  supportServers: {
    count: 0,
    next: '',
    previous: '',
    results: [],
  },
  isLoadingSupportServers: false,
  imagensLogsList: {
    count: 0,
    next: '',
    previous: '',
    results: [],
  },
  modelsList: {
    count: 0,
    next: '',
    previous: '',
    results: [],
  },
  imagensList: {
    count: 0,
    next: '',
    previous: '',
    results: [],
  },
  securityState: {
    activate: false,
    expire_days: 0,
    expire_passwords: false,
    min_length: 0,
    require_lower: false,
    require_num: false,
    require_symbol: false,
    require_upper: false,
    max_auth_tries: 0,
    check_complexity: false,
    check_history: false,
  },
  isLoadingSecurityState: false,
  environmentMaintenanceDisplayWarning: {
    active: false,
    start_date: '',
    end_date: '',
    message: '',
    display_to_logged_users: false,
  },
  environmentMaintenanceRemoveSessions: {
    enabled: false,
    session_removal_times_weekdays: [],
    session_removal_times_saturday: [],
    session_removal_times_sunday: [],
    session_removal_max_limit: 2,
    session_removal_message: '',
  },
  environmentMaintenanceBlockInstances: {
    enabled: false,
    auto_block_time: '',
  },
  sessionsInInstanceList: {
    count: 0,
    next: '',
    previous: '',
    loading: false,
    results: [],
  },
  environmentBehaviorWeek: {
    first_period: {
      is_active: false,
      hour: '',
      scale_instances_by_licences_margin: false,
      licences_margin_percent: 0,
      keep_support_servers: 'activated',
      application_server_mode: 'active',
    },
    second_period: {
      is_active: false,
      hour: '',
      scale_instances_by_licences_margin: false,
      licences_margin_percent: 0,
      keep_support_servers: 'activated',
      application_server_mode: 'active',
    },
  },
  urlsDashboard: {
    results: {
      results: [],
    },
  },
  sessionDatasDashboard: {
    results: {
      results: [
        {
          data: [],
          fields: [],
        },
      ],
    },
  },
  instanceDatasDashboard: {
    results: {
      results: [
        {
          data: [],
          fields: [],
        },
      ],
    },
  },
  environmentBehaviorSaturday: {
    first_period: {
      is_active: false,
      hour: '',
      scale_instances_by_licences_margin: false,
      licences_margin_percent: 0,
      keep_support_servers: 'activated',
      application_server_mode: 'active',
    },
    second_period: {
      is_active: false,
      hour: '',
      scale_instances_by_licences_margin: false,
      licences_margin_percent: 0,
      keep_support_servers: 'activated',
      application_server_mode: 'active',
    },
  },
  environmentBehaviorSunday: {
    first_period: {
      is_active: false,
      hour: '',
      scale_instances_by_licences_margin: false,
      licences_margin_percent: 0,
      keep_support_servers: 'activated',
      application_server_mode: 'active',
    },
    second_period: {
      is_active: false,
      hour: '',
      scale_instances_by_licences_margin: false,
      licences_margin_percent: 0,
      keep_support_servers: 'activated',
      application_server_mode: 'active',
    },
  },
  providerResources: {
    environment_uuid: null,
    keypairs: {
      results: null,
      isLoadingKeyPairs: false,
    },
    flavors: {
      results: null,
      isLoadingFlavors: false,
    },
    images: {
      results: null,
      isLoadingImages: false,
    },
    securityGroups: {
      results: null,
      isLoadingSecurityGroups: false,
    },
    zones: {
      results: null,
      isLoadingZones: false,
    },
    subnets: {
      results: null,
      isLoadingSubnets: false,
    },
    bootVolumes: { results: null, isLoadingBootVolumes: false },
    bootVolumeBackups: { results: null, isLoadingBootVolumeBackups: false },
    listProvidersResources: {
      results: null,
      isLoadingListProvidersResources: false,
    },
  },
  dashboardSessionsGraph: { metrics: [] },
  dashboardEnvironmentCostGraph: { total: '0', metrics: [] },
  periodFilter: PeriodCountAccess.LAST_24_HOURS,
  performanceDateFilter: {
    'start-date': '',
    'end-date': '',
  },
  periodFilterCosts: PeriodCountAccess.LAST_24_HOURS,
  environmentCostPeriodFilter: PeriodCountAccess.LAST_24_HOURS,
  environmentAvgMaxFilterCPU: AvgMaxCountAccess.AVG,
  environmentAvgMaxFilterMemory: AvgMaxCountAccess.AVG,
  environmentAvgMaxFilterOperationalTime: AvgMaxCountAccess.AVG,
  flavorsCostList: { count: 0, next: '', previous: '', results: [] },
  flavorsCostIsLoading: false,
  flavorsCostPeriodFilter: PeriodCountAccess.LAST_24_HOURS,
  isLoadingApps: false,
  applicationsList: { count: 0, next: '', previous: '', results: [] },
  isLoadingClientsApps: false,
  clientsApplicationsList: { count: 0, next: '', previous: '', results: [] },
  clientsPlatformApplicationsList: {
    count: 0,
    next: '',
    previous: '',
    results: [],
  },
  clientsToIncludeList: { count: 0, next: '', previous: '', results: [] },
  applicationsPlatformList: { count: 0, next: '', previous: '', results: [] },
  migrateErrors: [''],
  environmentPerformanceCPU: { metrics: [] },
  environmentPerformanceMemory: { metrics: [] },
  environmentPerformanceOperationalTime: { metrics: [] },
  environmentMonitoring: {
    enabled: false,
    instances_operational_timing_enabled: false,
    block_instances_with_sessions_timeout_enabled: false,
    cpu_benchmarks: {
      enabled: false,
      execution_interval_minutes: 60,
      duration_seconds: 60,
    },
  },
  monitoringHistory: {
    count: 0,
    next: '',
    previous: '',
    results: [],
  },
  publicationSchedule: {
    count: 0,
    next: '',
    previous: '',
    results: [],
  },
  isLoadingPublicationSchedule: false,
  isLoadingBulkClients: false,
  bulkClients: {
    count: 0,
    next: '',
    previous: '',
    results: [],
  },
  selectedEnvironment: null,
  behaviorReport: {} as BehaviorReportProps,
  isLoadingBehaviorReport: false,
  DNSReport: {} as DNSReportProps,
  isLoadingDNSReport: false,
  maintenanceReport: {} as MaintenanceReportProps,
  isLoadingMaintenanceReport: false,
  productReport: {} as ProductReportProps,
  isLoadingProductReport: false,
  productTypeReport: {} as ProductTypeReportProps,
  isLoadingProductTypeReport: false,
  providerReport: {} as ProviderReportProps,
  isLoadingProviderReport: false,
  regionReport: {} as RegionReportProps,
  isLoadingRegionReport: false,
  statusReport: {} as StatusReportProps,
  isLoadingStatusReport: false,
  virtualizerReport: {} as VirtualizerReportProps,
  isLoadingVirtualizerReport: false,
  zeroTrustReport: {} as ZeroTrustReportProps,
  isLoadingZeroTrustReport: false,
  connectionTypeEnvironmentReport: {} as ConnectionTypeEnvironmentReportProps,
  isLoadingConnectionTypeEnvironmentReport: false,
  timezonesList: { timezones: [] },
  selectedTimezone: null,
  validateZeroTrust: {} as ValidateZeroTrustFields,
  publishHistory: {} as PublicationResponse,
  environmentSessionsList: {} as ListEnvironmentSessionsProps,
  isLoadingEnvironmentSessionsList: false,
  listLicenseGroups: {} as ListLicenseGroupsProps,
  isLoadingLicenseGroups: false,
  licenseGroupDetails: {} as LicenseGroupsFields,
  isLoadingEnvironmentAdvisorsList: false,
  environmentAdvisorsList: {} as ListAdvisorsDataProps,
  adminPin: {} as AdminPinProps,
  isLoadingAdminPin: false,
  instanceObservability: {
    loading: false,
    error: false,
    data: null,
  } as InstanceObservabilityState,
};

export default handleActions(
  {
    [Types.NEW_ENVIRONMENT_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.NEW_ENVIRONMENT_FULFILLED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.NEW_ENVIRONMENT_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),

    [Types.TOGGLE_BLOCK_MULTIPLE_INSTANCES_REQUEST]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: true,
    }),
    [Types.TOGGLE_BLOCK_MULTIPLE_INSTANCES_FULFILLED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: false,
    }),
    [Types.TOGGLE_BLOCK_MULTIPLE_INSTANCES_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: false,
    }),

    [Types.GET_SESSIONS_CHART_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.GET_SESSIONS_CHART_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      sessionDatasDashboard: payload,
      isLoading: false,
    }),
    [Types.GET_SESSIONS_CHART_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),

    [Types.GET_INSTANCE_CHART_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.GET_INSTANCE_CHART_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      instanceDatasDashboard: payload,
      isLoading: false,
    }),
    [Types.GET_INSTANCE_CHART_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),

    [Types.GET_MAINTENANCE_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingMaintenance: true,
    }),
    [Types.GET_MAINTENANCE_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      ...payload,
      isLoadingMaintenance: false,
    }),
    [Types.GET_MAINTENANCE_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingMaintenance: false,
    }),

    [Types.EDIT_MAINTENANCE_ENVIRONMENTS_REQUEST]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: true,
    }),
    [Types.EDIT_MAINTENANCE_ENVIRONMENTS_FULFILLED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: false,
    }),
    [Types.EDIT_MAINTENANCE_ENVIRONMENTS_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: false,
    }),

    [Types.GET_HRLS_ENVIRONMENT_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.GET_HRLS_ENVIRONMENT_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      urlsDashboard: payload,
      isLoading: false,
    }),
    [Types.GET_HRLS_ENVIRONMENT_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),

    [Types.GET_ENVIRONMENT_BEHAVIOR_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.GET_ENVIRONMENT_BEHAVIOR_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      ...payload,
      isLoading: false,
    }),
    [Types.GET_ENVIRONMENT_BEHAVIOR_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),

    [Types.REGISTER_NEW_INSTANCE_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.REGISTER_NEW_INSTANCE_FULFILLED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.REGISTER_NEW_INSTANCE_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),

    [Types.UNLINK_INSTANCE_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.UNLINK_INSTANCE_FULFILLED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.UNLINK_INSTANCE_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),

    [Types.TERMINATE_INSTANCE_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.TERMINATE_INSTANCE_FULFILLED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.TERMINATE_INSTANCE_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),

    [Types.REBOOT_INSTANCE_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.REBOOT_INSTANCE_FULFILLED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.REBOOT_INSTANCE_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),

    [Types.ACTIVATE_INSTANCE_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.ACTIVATE_INSTANCE_FULFILLED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.ACTIVATE_INSTANCE_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),

    [Types.GET_ALL_SESSIONS_IN_INSTANCE_REQUEST]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      sessionsInInstanceList: {
        ...state.sessionsInInstanceList,
        loading: true,
      },
    }),
    [Types.GET_ALL_SESSIONS_IN_INSTANCE_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      sessionsInInstanceList: {
        ...payload,
        loading: false,
      },
    }),
    [Types.GET_ALL_SESSIONS_IN_INSTANCE_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      sessionsInInstanceList: {
        ...state.sessionsInInstanceList,
        loading: false,
      },
    }),

    [Types.GET_INSTANCE_OBSERVABILITY_REQUEST]: (
      state: EnvironmentsState,
      { payload }
    ) => {
      const preserve = Boolean(payload?.preserveExisting);
      return {
        ...state,
        instanceObservability: {
          loading: true,
          error: false,
          data: preserve ? state.instanceObservability.data : null,
        },
      };
    },
    [Types.GET_INSTANCE_OBSERVABILITY_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      instanceObservability: {
        loading: false,
        error: false,
        data: payload,
      },
    }),
    [Types.GET_INSTANCE_OBSERVABILITY_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      instanceObservability: {
        loading: false,
        error: true,
        data: state.instanceObservability.data,
      },
    }),

    [Types.EDIT_SECURITY_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.EDIT_SECURITY_FULFILLED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.EDIT_SECURITY_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),

    [Types.GET_ALL_ENVIRONMENTS_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.GET_ALL_ENVIRONMENTS_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      environmentList: payload,
      isLoading: false,
    }),
    [Types.GET_ALL_ENVIRONMENTS_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.GET_ALL_ENVIRONMENTS_OPERATOR_REQUEST]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: true,
    }),
    [Types.GET_ALL_ENVIRONMENTS_OPERATOR_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      environmentsList: payload,
      isLoading: false,
    }),
    [Types.GET_ALL_ENVIRONMENTS_OPERATOR_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: false,
    }),

    [Types.GET_ENVIRONMENT_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.GET_ENVIRONMENT_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      environmentSelected: payload,
      isLoading: false,
    }),
    [Types.GET_ENVIRONMENT_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      environmentSelected: null,
      isLoading: false,
    }),

    [Types.EDIT_ENVIRONMENT_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.EDIT_ENVIRONMENT_FULFILLED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.EDIT_ENVIRONMENT_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),

    [Types.GET_SECURITY_STATE_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingSecurityState: true,
    }),
    [Types.GET_SECURITY_STATE_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      securityState: payload,
      isLoadingSecurityState: false,
    }),
    [Types.GET_SECURITY_STATE_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingSecurityState: false,
    }),

    [Types.GET_ALL_INSTANCES_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      instancesList: {
        ...state.instancesList,
        loading: true,
      },
    }),
    [Types.GET_ALL_INSTANCES_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      instancesList: {
        ...payload,
        loading: false,
      },
    }),
    [Types.GET_ALL_INSTANCES_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      instancesList: {
        loading: false,
      },
    }),

    [Types.GET_PREPARATION_SERVER_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingPreparationServer: true,
    }),
    [Types.GET_PREPARATION_SERVER_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      isLoadingPreparationServer: false,
      preparationServer: payload,
    }),
    [Types.GET_PREPARATION_SERVER_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingPreparationServer: false,
    }),

    [Types.GET_SUPPORT_SERVERS_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingSupportServers: true,
    }),
    [Types.GET_SUPPORT_SERVERS_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      isLoadingSupportServers: false,
      supportServers: payload,
    }),
    [Types.GET_SUPPORT_SERVERS_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingSupportServers: false,
    }),

    [Types.GET_ALL_MODELS_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.GET_ALL_MODELS_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      modelsList: payload,
      isLoading: false,
    }),
    [Types.GET_ALL_MODELS_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),

    [Types.GET_MODEL_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      modelSelected: null,
      isLoading: true,
    }),
    [Types.GET_MODEL_FULFILLED]: (state: EnvironmentsState, { payload }) => ({
      ...state,
      modelSelected: payload,
      isLoading: false,
    }),
    [Types.GET_MODEL_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      modelSelected: null,
      isLoading: false,
    }),

    [Types.REGISTER_MODEL_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.REGISTER_MODEL_FULFILLED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.REGISTER_MODEL_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),

    [Types.EDIT_MODEL_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.EDIT_MODEL_FULFILLED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.EDIT_MODEL_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),

    [Types.REMOVE_MODEL_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.REMOVE_MODEL_FULFILLED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.REMOVE_MODEL_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.GET_ALARMS_INSTANCE_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.GET_ALARMS_INSTANCE_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      alarmsInstanceList: payload,
      isLoading: false,
    }),
    [Types.GET_ALARMS_INSTANCE_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),

    [Types.GET_ALL_IMAGENS_LOGS_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingLogs: true,
    }),
    [Types.GET_ALL_IMAGENS_LOGS_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      imagensLogsList: payload,
      isLoadingLogs: false,
    }),
    [Types.GET_ALL_IMAGENS_LOGS_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingLogs: false,
    }),

    [Types.SUBMIT_KEEP_USER_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.SUBMIT_KEEP_USER_FULFILLED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.SUBMIT_KEEP_USER_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),

    [Types.REQUESTING_USER_RECONNECTION_REQUEST]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: false,
    }),
    [Types.REQUESTING_USER_RECONNECTION_FULFILLED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: false,
    }),
    [Types.REQUESTING_USER_RECONNECTION_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: false,
    }),

    [Types.START_SERVER_REQUEST]: (state: EnvironmentsState, { payload }) => {
      if (payload.server_type === 'preparation') {
        return { ...state, isLoadingPreparationServer: true };
      }
      return { ...state, isLoadingSupportServers: true };
    },
    [Types.START_SERVER_FULFILLED]: (state: EnvironmentsState, { payload }) => {
      if (payload.server_type === 'preparation') {
        return { ...state, isLoadingPreparationServer: false };
      }
      return { ...state, isLoadingSupportServers: false };
    },
    [Types.START_SERVER_REJECTED]: (state: EnvironmentsState, { payload }) => {
      if (payload.server_type === 'preparation') {
        return { ...state, isLoadingPreparationServer: false };
      }
      return { ...state, isLoadingSupportServers: false };
    },

    [Types.STOP_SERVER_REQUEST]: (state: EnvironmentsState, { payload }) => {
      if (payload.server_type === 'preparation') {
        return { ...state, isLoadingPreparationServer: true };
      }
      return { ...state, isLoadingSupportServers: true };
    },
    [Types.STOP_SERVER_FULFILLED]: (state: EnvironmentsState, { payload }) => {
      if (payload.server_type === 'preparation') {
        return { ...state, isLoadingPreparationServer: false };
      }
      return { ...state, isLoadingSupportServers: false };
    },
    [Types.STOP_SERVER_REJECTED]: (state: EnvironmentsState, { payload }) => {
      if (payload.server_type === 'preparation') {
        return { ...state, isLoadingPreparationServer: false };
      }
      return { ...state, isLoadingSupportServers: false };
    },

    [Types.GET_PROVIDER_RESOURCES_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
      },
    }),
    [Types.GET_PROVIDER_RESOURCES_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        ...payload,
      },
    }),

    [Types.GET_PROVIDER_RESOURCES_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
      },
    }),

    [Types.GET_BOOT_VOLUMES_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        bootVolumes: {
          ...state.providerResources.bootVolumes,
          isLoadingBootVolumes: true,
        },
      },
    }),
    [Types.GET_BOOT_VOLUMES_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        bootVolumes: {
          results: payload,
          isLoadingBootVolumes: false,
        },
      },
    }),

    [Types.GET_BOOT_VOLUMES_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        bootVolumes: {
          ...state.providerResources.bootVolumes,
          isLoadingBootVolumes: false,
        },
      },
    }),

    [Types.GET_BOOT_VOLUME_BACKUPS_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        bootVolumeBackups: {
          ...state.providerResources.bootVolumeBackups,
          isLoadingBootVolumeBackups: true,
        },
      },
    }),
    [Types.GET_BOOT_VOLUME_BACKUPS_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        bootVolumeBackups: {
          results: payload,
          isLoadingBootVolumeBackups: false,
        },
      },
    }),

    [Types.GET_BOOT_VOLUME_BACKUPS_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        bootVolumeBackups: {
          ...state.providerResources.bootVolumeBackups,
          isLoadingBootVolumeBackups: false,
        },
      },
    }),

    [Types.GET_SECURITY_GROUPS_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        securityGroups: {
          ...state.providerResources.securityGroups,
          isLoadingSecurityGroups: true,
        },
      },
    }),
    [Types.GET_SECURITY_GROUPS_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        securityGroups: {
          results: payload,
          isLoadingSecurityGroups: false,
        },
      },
    }),

    [Types.GET_SECURITY_GROUPS_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        securityGroups: {
          ...state.providerResources.securityGroups,
          isLoadingSecurityGroups: false,
        },
      },
    }),

    [Types.GET_ZONES_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        zones: {
          ...state.providerResources.zones,
          isLoadingZones: true,
        },
      },
    }),
    [Types.GET_ZONES_FULFILLED]: (state: EnvironmentsState, { payload }) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        zones: {
          results: payload,
          isLoadingZones: false,
        },
      },
    }),

    [Types.GET_ZONES_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        zones: {
          ...state.providerResources.zones,
          isLoadingZones: false,
        },
      },
    }),

    [Types.GET_SUBNETS_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        subnets: {
          ...state.providerResources.subnets,
          isLoadingSubnets: true,
        },
      },
    }),
    [Types.GET_SUBNETS_FULFILLED]: (state: EnvironmentsState, { payload }) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        subnets: {
          results: payload,
          isLoadingSubnets: false,
        },
      },
    }),

    [Types.GET_SUBNETS_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        subnets: {
          ...state.providerResources.subnets,
          isLoadingSubnets: false,
        },
      },
    }),

    [Types.GET_KEY_PAIRS_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        keypairs: {
          ...state.providerResources.keypairs,
          isLoadingKeyPairs: true,
        },
      },
    }),
    [Types.GET_KEY_PAIRS_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        keypairs: {
          results: payload,
          isLoadingKeyPairs: false,
        },
      },
    }),

    [Types.GET_KEY_PAIRS_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        keypairs: {
          ...state.providerResources.keypairs,
          isLoadingKeyPairs: false,
        },
      },
    }),

    [Types.GET_IMAGES_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        images: {
          ...state.providerResources.images,
          isLoadingImages: true,
        },
      },
    }),
    [Types.GET_IMAGES_FULFILLED]: (state: EnvironmentsState, { payload }) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        images: {
          results: payload,
          isLoadingImages: false,
        },
      },
    }),

    [Types.GET_IMAGES_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        images: {
          ...state.providerResources.images,
          isLoadingImages: false,
        },
      },
    }),

    [Types.GET_LIST_PROVIDER_RESOURCES_REQUEST]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        listProvidersResources: {
          ...state.providerResources.listProvidersResources,
          isLoadingListProvidersResources: true,
        },
      },
    }),
    [Types.GET_LIST_PROVIDER_RESOURCES_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        listProvidersResources: {
          results: payload,
          isLoadingListProvidersResources: false,
        },
      },
    }),

    [Types.GET_LIST_PROVIDER_RESOURCES_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        listProvidersResources: {
          ...state.providerResources.listProvidersResources,
          isLoadingListProvidersResources: false,
        },
      },
    }),

    [Types.GET_FLAVORS_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        flavors: {
          ...state.providerResources.flavors,
          isLoadingFlavors: true,
        },
      },
    }),
    [Types.GET_FLAVORS_FULFILLED]: (state: EnvironmentsState, { payload }) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        flavors: {
          results: payload,
          isLoadingFlavors: false,
        },
      },
    }),

    [Types.GET_FLAVORS_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      providerResources: {
        ...state.providerResources,
        flavors: {
          ...state.providerResources.flavors,
          isLoadingFlavors: false,
        },
      },
    }),

    [Types.STOP_TIME_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
      isLoadingPreparationServer: true,
    }),
    [Types.STOP_TIME_FULFILLED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
      isLoadingPreparationServer: false,
    }),
    [Types.STOP_TIME_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
      isLoadingPreparationServer: false,
    }),

    [Types.CLEAN_ENVIRONMENT_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      environmentSelected: null,
    }),
    [Types.GET_DASHBOARD_SESSIONS_GRAPH_REQUEST]: (
      state: EnvironmentsState
    ) => ({
      ...state,
    }),
    [Types.GET_DASHBOARD_ENVIRONMENT_COST_GRAPH_REQUEST]: (
      state: EnvironmentsState
    ) => ({
      ...state,
    }),

    [Types.GET_DASHBOARD_SESSIONS_GRAPH_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      dashboardSessionsGraph: payload,
    }),
    [Types.GET_DASHBOARD_ENVIRONMENT_COST_GRAPH_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      dashboardEnvironmentCostGraph: payload,
    }),

    [Types.GET_DASHBOARD_SESSIONS_GRAPH_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
    }),
    [Types.GET_DASHBOARD_ENVIRONMENT_COST_GRAPH_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
    }),

    [Types.SET_PERIOD_FILTER]: (state: EnvironmentsState, { payload }) => ({
      ...state,
      periodFilter: payload,
    }),
    [Types.SET_PERFORMANCE_DATE_FILTER]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      performanceDateFilter: payload,
    }),
    [Types.SET_ENVIRONMENT_COST_PERIOD_FILTER]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      periodFilterCosts: payload,
    }),
    [Types.SET_ENVIRONMENT_AVG_MAX_FILTER_CPU]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      environmentAvgMaxFilterCPU: payload,
    }),
    [Types.SET_ENVIRONMENT_AVG_MAX_FILTER_MEMORY]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      environmentAvgMaxFilterMemory: payload,
    }),
    [Types.SET_ENVIRONMENT_AVG_MAX_FILTER_OPERATIONAL_TIME]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      environmentAvgMaxFilterOperationalTime: payload,
    }),
    [Types.GET_FLAVORS_COST_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      flavorsCostIsLoading: true,
    }),
    [Types.GET_FLAVORS_COST_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      flavorsCostList: payload,
      flavorsCostIsLoading: false,
    }),
    [Types.GET_FLAVORS_COST_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      flavorsCostIsLoading: false,
    }),
    [Types.SET_FLAVORS_COST_PERIOD_FILTER]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      periodFilter: payload,
    }),
    [Types.GET_ALL_APPLICATIONS_AUTOSKY_REQUEST]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingApps: true,
    }),
    [Types.GET_ALL_APPLICATIONS_AUTOSKY_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      applicationsList: payload,
      isLoadingApps: false,
    }),
    [Types.GET_ALL_APPLICATIONS_AUTOSKY_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingApps: false,
    }),

    [Types.GET_CLIENTS_APPLICATIONS_AUTOSKY_REQUEST]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingClientsApps: true,
    }),
    [Types.GET_CLIENTS_APPLICATIONS_AUTOSKY_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      clientsApplicationsList: payload,
      isLoadingClientsApps: false,
    }),
    [Types.GET_CLIENTS_APPLICATIONS_AUTOSKY_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingClientsApps: false,
    }),
    [Types.RENAME_CLIENTS_APPLICATIONS_AUTOSKY_REQUEST]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingClientsApps: true,
    }),
    [Types.RENAME_CLIENTS_APPLICATIONS_AUTOSKY_FULFILLED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingClientsApps: false,
    }),
    [Types.RENAME_CLIENTS_APPLICATIONS_AUTOSKY_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingClientsApps: false,
    }),
    [Types.REMOVE_CLIENTS_APPLICATIONS_AUTOSKY_REQUEST]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingClientsApps: true,
    }),
    [Types.REMOVE_CLIENTS_APPLICATIONS_AUTOSKY_FULFILLED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingClientsApps: false,
    }),
    [Types.REMOVE_CLIENTS_APPLICATIONS_AUTOSKY_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingClientsApps: false,
    }),
    [Types.CHANGE_STATE_CLIENTS_APPLICATIONS_AUTOSKY_REQUEST]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingClientsApps: true,
    }),
    [Types.CHANGE_STATE_CLIENTS_APPLICATIONS_AUTOSKY_FULFILLED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingClientsApps: false,
    }),
    [Types.CHANGE_STATE_CLIENTS_APPLICATIONS_AUTOSKY_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingClientsApps: false,
    }),

    [Types.INCLUDE_CLIENTS_APPLICATIONS_AUTOSKY_REQUEST]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingClientsApps: true,
    }),
    [Types.INCLUDE_CLIENTS_APPLICATIONS_AUTOSKY_FULFILLED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingClientsApps: false,
    }),
    [Types.INCLUDE_CLIENTS_APPLICATIONS_AUTOSKY_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingClientsApps: false,
    }),

    [Types.GET_CLIENTS_TO_INCLUDE_APPLICATIONS_AUTOSKY_REQUEST]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingClientsApps: true,
    }),
    [Types.GET_CLIENTS_TO_INCLUDE_APPLICATIONS_AUTOSKY_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      clientsToIncludeList: payload,
      isLoadingClientsApps: false,
    }),
    [Types.GET_CLIENTS_TO_INCLUDE_APPLICATIONS_AUTOSKY_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingClientsApps: false,
    }),

    [Types.GET_ALL_APPLICATIONS_PLATFORM_REQUEST]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingApps: true,
    }),
    [Types.GET_ALL_APPLICATIONS_PLATFORM_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      applicationsPlatformList: payload,
      isLoadingApps: false,
    }),
    [Types.GET_ALL_APPLICATIONS_PLATFORM_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingApps: false,
    }),

    [Types.GET_CLIENTS_APPLICATIONS_PLATFORM_REQUEST]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingClientsApps: true,
    }),
    [Types.GET_CLIENTS_APPLICATIONS_PLATFORM_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      clientsPlatformApplicationsList: payload,
      isLoadingClientsApps: false,
    }),
    [Types.GET_CLIENTS_APPLICATIONS_PLATFORM_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingClientsApps: false,
    }),

    [Types.REMOVE_CLIENTS_APPLICATIONS_PLATFORM_REQUEST]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingApps: true,
    }),
    [Types.REMOVE_CLIENTS_APPLICATIONS_PLATFORM_FULFILLED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingApps: false,
    }),
    [Types.REMOVE_CLIENTS_APPLICATIONS_PLATFORM_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingApps: false,
    }),
    [Types.MIGRATE_ENVIRONMENT_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.MIGRATE_ENVIRONMENT_FULFILLED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.MIGRATE_ENVIRONMENT_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.SET_MIGRATE_ERRORS]: (state: EnvironmentsState, { payload }) => ({
      ...state,
      migrateErrors: payload,
      isLoading: false,
    }),

    [Types.PUBLISH_NEW_IMAGE_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingPreparationServer: true,
    }),
    [Types.PUBLISH_NEW_IMAGE_FULFILLED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingPreparationServer: false,
    }),
    [Types.PUBLISH_NEW_IMAGE_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingPreparationServer: false,
    }),

    [Types.ATTACH_SERVER_REQUEST]: (state: EnvironmentsState, { payload }) => {
      if (payload.type === 'preparation') {
        return { ...state, isLoadingPreparationServer: true };
      }
      return { ...state, isLoadingSupportServers: true };
    },
    [Types.ATTACH_SERVER_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => {
      if (payload === 'preparation') {
        return { ...state, isLoadingPreparationServer: false };
      }
      return { ...state, isLoadingSupportServers: false };
    },
    [Types.ATTACH_SERVER_REJECTED]: (state: EnvironmentsState, { payload }) => {
      if (payload === 'preparation') {
        return { ...state, isLoadingPreparationServer: false };
      }
      return { ...state, isLoadingSupportServers: false };
    },

    [Types.DETACH_SERVER_REQUEST]: (state: EnvironmentsState, { payload }) => {
      if (payload.type === 'preparation') {
        return { ...state, isLoadingPreparationServer: true };
      }
      return { ...state, isLoadingSupportServers: true };
    },
    [Types.DETACH_SERVER_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => {
      if (payload === 'preparation') {
        return { ...state, isLoadingPreparationServer: false };
      }
      return { ...state, isLoadingSupportServers: false };
    },
    [Types.DETACH_SERVER_REJECTED]: (state: EnvironmentsState, { payload }) => {
      if (payload === 'preparation') {
        return { ...state, isLoadingPreparationServer: false };
      }
      return { ...state, isLoadingSupportServers: false };
    },
    [Types.GET_ENVIRONMENT_MONITORING_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.GET_ENVIRONMENT_MONITORING_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      environmentMonitoring: payload,
      isLoading: false,
    }),
    [Types.GET_ENVIRONMENT_MONITORING_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: false,
    }),

    [Types.EDIT_ENVIRONMENT_MONITORING_REQUEST]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: true,
    }),
    [Types.EDIT_ENVIRONMENT_MONITORING_FULFILLED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: false,
    }),
    [Types.EDIT_ENVIRONMENT_MONITORING_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: false,
    }),
    [Types.GET_MONITORING_HISTORY_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.GET_MONITORING_HISTORY_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      monitoringHistory: payload,
      isLoading: false,
    }),
    [Types.GET_MONITORING_HISTORY_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),

    [Types.GET_ENVIRONMENT_PERFORMANCE_CPU_REQUEST]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: true,
    }),
    [Types.GET_ENVIRONMENT_PERFORMANCE_CPU_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      isLoading: false,
      environmentPerformanceCPU: payload,
    }),
    [Types.GET_ENVIRONMENT_PERFORMANCE_CPU_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: false,
    }),

    [Types.GET_ENVIRONMENT_PERFORMANCE_MEMORY_REQUEST]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: true,
    }),
    [Types.GET_ENVIRONMENT_PERFORMANCE_MEMORY_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      isLoading: false,
      environmentPerformanceMemory: payload,
    }),
    [Types.GET_ENVIRONMENT_PERFORMANCE_MEMORY_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: false,
    }),
    [Types.GET_ENVIRONMENT_PERFORMANCE_OPERATIONAL_TIME_REQUEST]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: true,
    }),
    [Types.GET_ENVIRONMENT_PERFORMANCE_OPERATIONAL_TIME_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      isLoading: false,
      environmentPerformanceOperationalTime: payload,
    }),
    [Types.GET_ENVIRONMENT_PERFORMANCE_OPERATIONAL_TIME_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: false,
    }),
    [Types.GET_PUBLICATION_SCHEDULE_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingPublicationSchedule: true,
    }),
    [Types.GET_PUBLICATION_SCHEDULE_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      isLoadingPublicationSchedule: false,
      publicationSchedule: payload,
    }),
    [Types.GET_PUBLICATION_SCHEDULE_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingPublicationSchedule: false,
    }),
    [Types.SET_PUBLICATION_SCHEDULE_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingPublicationSchedule: true,
    }),
    [Types.SET_PUBLICATION_SCHEDULE_FULFILLED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingPublicationSchedule: false,
    }),
    [Types.SET_PUBLICATION_SCHEDULE_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingPublicationSchedule: false,
    }),
    [Types.CANCEL_PUBLICATION_SCHEDULE_REQUEST]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingPublicationSchedule: true,
    }),
    [Types.CANCEL_PUBLICATION_SCHEDULE_FULFILLED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingPublicationSchedule: false,
    }),
    [Types.CANCEL_PUBLICATION_SCHEDULE_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingPublicationSchedule: false,
    }),
    [Types.GET_BULK_CLIENTS_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingBulkClients: true,
    }),
    [Types.GET_BULK_CLIENTS_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      isLoadingBulkClients: false,
      bulkClients: payload,
    }),
    [Types.GET_BULK_CLIENTS_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingBulkClients: false,
    }),
    [Types.EDIT_BULK_CLIENTS_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingBulkClients: true,
    }),
    [Types.EDIT_BULK_CLIENTS_FULFILLED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingBulkClients: false,
    }),
    [Types.EDIT_BULK_CLIENTS_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingBulkClients: false,
    }),

    [Types.SET_SELECTED_ENVIRONMENT]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      selectedEnvironment: payload,
    }),
    [Types.SET_PRODUCTION_ENVIRONMENT_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.ACCEPT_STATUS_PENDING_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.ACCEPT_STATUS_PENDING_FULFILLED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.ACCEPT_STATUS_PENDING_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.GET_BEHAVIOR_REPORT_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      isLoadingBehaviorReport: false,
      behaviorReport: payload,
    }),
    [Types.GET_BEHAVIOR_REPORT_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingBehaviorReport: false,
    }),
    [Types.GET_BEHAVIOR_REPORT_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingBehaviorReport: true,
    }),
    [Types.GET_DNS_REPORT_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      isLoadingDNSReport: false,
      DNSReport: payload,
    }),
    [Types.GET_DNS_REPORT_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingDNSReport: false,
    }),
    [Types.GET_DNS_REPORT_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingDNSReport: true,
    }),
    [Types.GET_MAINTENANCE_REPORT_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      isLoadingMaintenanceReport: false,
      maintenanceReport: payload,
    }),
    [Types.GET_MAINTENANCE_REPORT_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingMaintenanceReport: false,
    }),
    [Types.GET_MAINTENANCE_REPORT_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingMaintenanceReport: true,
    }),
    [Types.GET_PRODUCT_REPORT_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      isLoadingProductReport: false,
      productReport: payload,
    }),
    [Types.GET_PRODUCT_REPORT_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingProductReport: false,
    }),
    [Types.GET_PRODUCT_REPORT_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingProductReport: true,
    }),
    [Types.GET_PRODUCT_TYPE_REPORT_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      isLoadingProductTypeReport: false,
      productTypeReport: payload,
    }),
    [Types.GET_PRODUCT_TYPE_REPORT_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingProductTypeReport: false,
    }),
    [Types.GET_PRODUCT_TYPE_REPORT_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingProductTypeReport: true,
    }),
    [Types.GET_PROVIDER_REPORT_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      isLoadingProviderReport: false,
      providerReport: payload,
    }),
    [Types.GET_PROVIDER_REPORT_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingProviderReport: false,
    }),
    [Types.GET_PROVIDER_REPORT_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingProviderReport: true,
    }),
    [Types.GET_REGION_REPORT_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      isLoadingRegionReport: false,
      regionReport: payload,
    }),
    [Types.GET_REGION_REPORT_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingRegionReport: false,
    }),
    [Types.GET_REGION_REPORT_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingRegionReport: true,
    }),
    [Types.GET_STATUS_REPORT_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      isLoadingStatusReport: false,
      statusReport: payload,
    }),
    [Types.GET_STATUS_REPORT_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingStatusReport: false,
    }),
    [Types.GET_STATUS_REPORT_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingStatusReport: true,
    }),
    [Types.GET_VIRTUALIZER_REPORT_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      isLoadingVirtualizerReport: false,
      virtualizerReport: payload,
    }),
    [Types.GET_VIRTUALIZER_REPORT_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingVirtualizerReport: false,
    }),
    [Types.GET_VIRTUALIZER_REPORT_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingVirtualizerReport: true,
    }),
    [Types.GET_ZERO_TRUST_REPORT_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      isLoadingZeroTrustReport: false,
      zeroTrustReport: payload,
    }),
    [Types.GET_ZERO_TRUST_REPORT_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingZeroTrustReport: false,
    }),
    [Types.GET_ZERO_TRUST_REPORT_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingZeroTrustReport: true,
    }),
    [Types.GET_TIMEZONES_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.GET_TIMEZONES_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      isLoading: false,
      timezonesList: payload,
    }),
    [Types.GET_TIMEZONES_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.SET_SELECTED_TIMEZONE]: (state: EnvironmentsState, { payload }) => ({
      ...state,
      selectedTimezone: payload,
    }),
    [Types.EXTEND_COMPLETION_DATE_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.EXTEND_COMPLETION_DATE_FULFILLED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.EXTEND_COMPLETION_DATE_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.EXPORT_ENVIRONMENT_SUMMARY_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.EXPORT_ENVIRONMENT_SUMMARY_FULFILLED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: false,
    }),
    [Types.EXPORT_ENVIRONMENT_SUMMARY_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: false,
    }),
    [Types.VALIDATE_ZERO_TRUST_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.VALIDATE_ZERO_TRUST_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      isLoading: false,
      validateZeroTrust: payload,
    }),
    [Types.VALIDATE_ZERO_TRUST_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.PUBLISH_IMAGE_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.PUBLISH_IMAGE_FULFILLED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.PUBLISH_IMAGE_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.GET_PUBLISH_HISTORY_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.GET_PUBLISH_HISTORY_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      publishHistory: payload,
      isLoading: false,
    }),
    [Types.GET_PUBLISH_HISTORY_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.EXPORT_HISTORY_MONITORING_CSV_REQUEST]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: true,
    }),
    [Types.EXPORT_HISTORY_MONITORING_CSV_FULFILLED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: false,
    }),
    [Types.EXPORT_HISTORY_MONITORING_CSV_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoading: false,
    }),

    [Types.BLOCK_SERVER_ACTIONS_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.BLOCK_SERVER_ACTIONS_FULFILLED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.BLOCK_SERVER_ACTIONS_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.UNBLOCK_SERVER_ACTIONS_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: true,
    }),
    [Types.UNBLOCK_SERVER_ACTIONS_FULFILLED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.UNBLOCK_SERVER_ACTIONS_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoading: false,
    }),
    [Types.GET_ENVIRONMENT_SESSIONS_REQUEST]: (
      state: EnvironmentsState
    ): EnvironmentsState => ({
      ...state,
      isLoadingEnvironmentSessionsList: true,
    }),
    [Types.GET_ENVIRONMENT_SESSIONS_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ): EnvironmentsState => ({
      ...state,
      environmentSessionsList: payload,
      isLoadingEnvironmentSessionsList: false,
    }),
    [Types.GET_ENVIRONMENT_SESSIONS_REJECTED]: (
      state: EnvironmentsState
    ): EnvironmentsState => ({
      ...state,
      isLoadingEnvironmentSessionsList: false,
    }),
    [Types.GET_LICENSE_GROUPS_REQUEST]: (
      state: EnvironmentsState
    ): EnvironmentsState => ({
      ...state,
      isLoadingLicenseGroups: true,
    }),
    [Types.GET_LICENSE_GROUPS_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ): EnvironmentsState => ({
      ...state,
      listLicenseGroups: payload,
      isLoadingLicenseGroups: false,
    }),
    [Types.GET_LICENSE_GROUPS_REJECTED]: (
      state: EnvironmentsState
    ): EnvironmentsState => ({
      ...state,
      isLoadingLicenseGroups: false,
    }),
    [Types.CREATE_LICENSE_GROUP_REQUEST]: (
      state: EnvironmentsState
    ): EnvironmentsState => ({
      ...state,
      isLoadingLicenseGroups: true,
    }),
    [Types.CREATE_LICENSE_GROUP_FULFILLED]: (
      state: EnvironmentsState
    ): EnvironmentsState => ({
      ...state,
      isLoadingLicenseGroups: false,
    }),
    [Types.CREATE_LICENSE_GROUP_REJECTED]: (
      state: EnvironmentsState
    ): EnvironmentsState => ({
      ...state,
      isLoadingLicenseGroups: false,
    }),
    [Types.GET_LICENSE_GROUP_DETAILS_REQUEST]: (
      state: EnvironmentsState
    ): EnvironmentsState => ({
      ...state,
      isLoadingLicenseGroups: true,
    }),
    [Types.GET_LICENSE_GROUP_DETAILS_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ): EnvironmentsState => ({
      ...state,
      licenseGroupDetails: payload,
      isLoadingLicenseGroups: false,
    }),
    [Types.GET_LICENSE_GROUP_DETAILS_REJECTED]: (
      state: EnvironmentsState
    ): EnvironmentsState => ({
      ...state,
      isLoadingLicenseGroups: false,
    }),
    [Types.EDIT_LICENSE_GROUP_REQUEST]: (
      state: EnvironmentsState
    ): EnvironmentsState => ({
      ...state,
      isLoadingLicenseGroups: true,
    }),
    [Types.EDIT_LICENSE_GROUP_FULFILLED]: (
      state: EnvironmentsState
    ): EnvironmentsState => ({
      ...state,
      isLoadingLicenseGroups: false,
    }),
    [Types.EDIT_LICENSE_GROUP_REJECTED]: (
      state: EnvironmentsState
    ): EnvironmentsState => ({
      ...state,
      isLoadingLicenseGroups: false,
    }),
    [Types.DELETE_LICENSE_GROUP_REQUEST]: (
      state: EnvironmentsState
    ): EnvironmentsState => ({
      ...state,
      isLoadingLicenseGroups: true,
    }),
    [Types.DELETE_LICENSE_GROUP_FULFILLED]: (
      state: EnvironmentsState
    ): EnvironmentsState => ({
      ...state,
      isLoadingLicenseGroups: false,
    }),
    [Types.DELETE_LICENSE_GROUP_REJECTED]: (
      state: EnvironmentsState
    ): EnvironmentsState => ({
      ...state,
      isLoadingLicenseGroups: false,
    }),
    [Types.ADD_CLIENTS_TO_LICENSE_GROUP_REQUEST]: (
      state: EnvironmentsState
    ): EnvironmentsState => ({
      ...state,
      isLoadingLicenseGroups: true,
    }),
    [Types.ADD_CLIENTS_TO_LICENSE_GROUP_FULFILLED]: (
      state: EnvironmentsState
    ): EnvironmentsState => ({
      ...state,
      isLoadingLicenseGroups: false,
    }),
    [Types.ADD_CLIENTS_TO_LICENSE_GROUP_REJECTED]: (
      state: EnvironmentsState
    ): EnvironmentsState => ({
      ...state,
      isLoadingLicenseGroups: false,
    }),
    [Types.FULL_UPDATE_LICENSE_GROUP_CLIENTS_REQUEST]: (
      state: EnvironmentsState
    ): EnvironmentsState => ({
      ...state,
      isLoadingLicenseGroups: true,
    }),
    [Types.FULL_UPDATE_LICENSE_GROUP_CLIENTS_FULFILLED]: (
      state: EnvironmentsState
    ): EnvironmentsState => ({
      ...state,
      isLoadingLicenseGroups: false,
    }),
    [Types.FULL_UPDATE_LICENSE_GROUP_CLIENTS_REJECTED]: (
      state: EnvironmentsState
    ): EnvironmentsState => ({
      ...state,
      isLoadingLicenseGroups: false,
    }),
    [Types.GET_CONNECTION_TYPE_ENVIRONMENT_REPORT_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => ({
      ...state,
      isLoadingConnectionTypeEnvironmentReport: false,
      connectionTypeEnvironmentReport: payload,
    }),
    [Types.GET_CONNECTION_TYPE_ENVIRONMENT_REPORT_REJECTED]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingConnectionTypeEnvironmentReport: false,
    }),
    [Types.GET_CONNECTION_TYPE_ENVIRONMENT_REPORT_REQUEST]: (
      state: EnvironmentsState
    ) => ({
      ...state,
      isLoadingConnectionTypeEnvironmentReport: true,
    }),

    [Types.GET_ENVIRONMENT_ADVISORS_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingEnvironmentAdvisorsList: true,
    }),
    [Types.GET_ENVIRONMENT_ADVISORS_FULFILLED]: (
      state: EnvironmentsState,
      { payload }: { payload: ListAdvisorsDataProps }
    ) => ({
      ...state,
      isLoadingEnvironmentAdvisorsList: false,
      environmentAdvisorsList: payload,
    }),
    [Types.GET_ENVIRONMENT_ADVISORS_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingEnvironmentAdvisorsList: false,
    }),

    [Types.GET_ADMIN_PIN_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingAdminPin: true,
    }),
    [Types.GET_ADMIN_PIN_FULFILLED]: (
      state: EnvironmentsState,
      { payload }: { payload: ListAdvisorsDataProps }
    ) => ({
      ...state,
      isLoadingAdminPin: false,
      adminPin: payload,
    }),
    [Types.GET_ADMIN_PIN_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingAdminPin: false,
    }),

    [Types.RESET_ADMIN_PIN_REQUEST]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingAdminPin: true,
    }),
    [Types.RESET_ADMIN_PIN_FULFILLED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingAdminPin: false,
    }),
    [Types.RESET_ADMIN_PIN_REJECTED]: (state: EnvironmentsState) => ({
      ...state,
      isLoadingAdminPin: false,
    }),

    [Types.ENABLE_SERVER_MONITORING_MANAGEMENT_REQUEST]: (
      state: EnvironmentsState,
      { payload }
    ) => {
      if (payload.type === 'preparation') {
        return { ...state, isLoadingPreparationServer: true };
      }
      return { ...state, isLoadingSupportServers: true };
    },
    [Types.ENABLE_SERVER_MONITORING_MANAGEMENT_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => {
      if (payload.type === 'preparation') {
        return { ...state, isLoadingPreparationServer: false };
      }
      return { ...state, isLoadingSupportServers: false };
    },
    [Types.ENABLE_SERVER_MONITORING_MANAGEMENT_REJECTED]: (
      state: EnvironmentsState,
      { payload }
    ) => {
      if (payload.type === 'preparation') {
        return { ...state, isLoadingPreparationServer: false };
      }
      return { ...state, isLoadingSupportServers: false };
    },

    [Types.DISABLE_SERVER_MONITORING_MANAGEMENT_REQUEST]: (
      state: EnvironmentsState,
      { payload }
    ) => {
      if (payload.type === 'preparation') {
        return { ...state, isLoadingPreparationServer: true };
      }
      return { ...state, isLoadingSupportServers: true };
    },
    [Types.DISABLE_SERVER_MONITORING_MANAGEMENT_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ) => {
      if (payload.type === 'preparation') {
        return { ...state, isLoadingPreparationServer: false };
      }
      return { ...state, isLoadingSupportServers: false };
    },
    [Types.DISABLE_SERVER_MONITORING_MANAGEMENT_REJECTED]: (
      state: EnvironmentsState,
      { payload }
    ) => {
      if (payload.type === 'preparation') {
        return { ...state, isLoadingPreparationServer: false };
      }
      return { ...state, isLoadingSupportServers: false };
    },

    [Types.GET_SERVER_MONITORING_STATUS_REQUEST]: (
      state: EnvironmentsState,
      { payload }
    ): EnvironmentsState => ({
      ...state,
      supportServers: {
        ...state.supportServers,
        results: state.supportServers.results.map(server => {
          if (server.instance_id === payload.serverId) {
            return {
              ...server,
              isLoadingMonitoringStatus: true,
              monitoringStatus: '',
            };
          }
          return server;
        }),
      },
    }),
    [Types.GET_SERVER_MONITORING_STATUS_FULFILLED]: (
      state: EnvironmentsState,
      { payload }
    ): EnvironmentsState => ({
      ...state,
      supportServers: {
        ...state.supportServers,
        results: state.supportServers.results.map(server => {
          if (server.instance_id === payload.serverId) {
            return {
              ...server,
              isLoadingMonitoringStatus: false,
              monitoringStatus: payload.data.status,
            };
          }
          return server;
        }),
      },
    }),
    [Types.GET_SERVER_MONITORING_STATUS_REJECTED]: (
      state: EnvironmentsState,
      { payload }
    ): EnvironmentsState => ({
      ...state,
      supportServers: {
        ...state.supportServers,
        results: state.supportServers.results.map(server => {
          if (server.instance_id === payload.serverId) {
            return {
              ...server,
              isLoadingMonitoringStatus: false,
              monitoringStatus: 'unavailable',
            };
          }
          return server;
        }),
      },
    }),
  },
  initialState
);
